import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "w-full px-6" }
const _hoisted_2 = { class: "mb-6 flex flex-wrap items-center gap-3" }
const _hoisted_3 = {
  key: 0,
  class: "hidden md:block bg-white rounded-lg border border-gray-200 shadow-sm"
}
const _hoisted_4 = { class: "text-center" }
const _hoisted_5 = { class: "flex items-center gap-2" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = ["onClick"]
const _hoisted_8 = {
  key: 1,
  class: "inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-xs font-medium text-gray-500",
  title: "La marque par défaut ne peut pas être supprimée"
}
const _hoisted_9 = {
  key: 1,
  class: "md:hidden space-y-3"
}
const _hoisted_10 = { class: "flex items-center justify-between gap-3 mb-3" }
const _hoisted_11 = { class: "min-w-0" }
const _hoisted_12 = { class: "text-base font-semibold text-black truncate" }
const _hoisted_13 = {
  key: 0,
  class: "mt-1 inline-flex px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium"
}
const _hoisted_14 = { class: "flex items-center justify-between text-sm text-black mb-4" }
const _hoisted_15 = { class: "flex items-center justify-between text-sm text-black mb-4" }
const _hoisted_16 = { class: "flex items-center gap-2" }
const _hoisted_17 = ["onClick"]
const _hoisted_18 = ["onClick"]
const _hoisted_19 = {
  key: 1,
  class: "inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-xs font-medium text-gray-500"
}
const _hoisted_20 = {
  key: 2,
  class: "bg-white rounded-lg border border-gray-200 p-8 text-center"
}
const _hoisted_21 = { class: "flex items-center space-x-2" }
const _hoisted_22 = { class: "flex justify-end gap-3" }
const _hoisted_23 = { class: "text-center" }
const _hoisted_24 = { class: "font-semibold text-black mb-4" }
const _hoisted_25 = { class: "flex justify-end gap-3" }
const _hoisted_26 = { class: "text-black mb-2 text-center" }
const _hoisted_27 = { class: "font-semibold" }
const _hoisted_28 = { class: "font-semibold" }
const _hoisted_29 = ["value"]
const _hoisted_30 = { class: "flex justify-end gap-3" }

import { ref, reactive, computed, onMounted } from 'vue'
import { PageHeader, ActionButton, BaseInput, ModalComp, ReusableTable } from '@/components'
import BrandStatusSwitch from '@/components/account/BrandStatusSwitch.vue'
import { getVendeurMarques, createVendeurMarque, updateVendeurMarque, deleteVendeurMarque, activerVendeurMarques, setVendeurMarqueStatut } from '@/api/brands'
import type { Brand } from '@/types'
import {useAuthStore} from "@/stores/auth";
import {useRouter} from "vue-router";
import { useNotification } from '@/composables/useNotification'


export default /*@__PURE__*/_defineComponent({
  __name: 'MyBrandsView',
  setup(__props) {

const authStore = useAuthStore()
const router = useRouter()
const notification = useNotification()

const useBrandStore = () => {
  const brands = ref<Brand[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const fetchBrands = async (throwOnError = false) => {
    isLoading.value = true
    error.value = null
    
    try {
      const marques = await getVendeurMarques()
      // Trier les marques : marque par défaut en premier, puis par nom
      brands.value = marques.sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1
        if (!a.isDefault && b.isDefault) return 1
        return a.name.localeCompare(b.name)
      })

      // Sécurité: s'il y a des marques mais aucune par défaut, définir la première comme défaut
      if (brands.value.length > 0 && !brands.value.some(b => b.isDefault)) {
        try {
          const first = brands.value[0]
          const updated = await updateVendeurMarque(first.id, undefined, true)
          brands.value[0] = {
            ...first,
            ...updated,
            isActive: first.isActive,
            productCount: first.productCount
          }
          // Mettre les autres en non défaut dans l'état local
          for (let i = 1; i < brands.value.length; i++) brands.value[i].isDefault = false
        } catch (e) {
          console.warn('Impossible de définir une marque par défaut automatiquement:', e)
        }
      }
    } catch (err) {
      error.value = 'Erreur lors du chargement des marques'
      console.error('Erreur fetchBrands:', err)
      if (throwOnError) throw err
    } finally {
      isLoading.value = false
    }
  }

  const addBrand = async (brandData: { name: string; isDefault: boolean }) => {
    try {
      // Si c'est la première marque, elle doit être par défaut
      if (brands.value.length === 0) {
        brandData.isDefault = true
      }
      const newBrand = await createVendeurMarque(brandData.name, brandData.isDefault)
      await fetchBrands(true)
      return newBrand
    } catch (err) {
      error.value = 'Erreur lors de l\'ajout de la marque'
      throw err
    }
  }

  const updateBrand = async (id: string, brandData: Partial<Brand>) => {
    try {
      // Empêcher d'enlever le statut défaut si c'est la seule marque par défaut
      if (brandData.isDefault === false) {
        const target = brands.value.find(b => b.id === id)
        const defaults = brands.value.filter(b => b.isDefault)
        if (target?.isDefault && defaults.length === 1) {
          notification.error('Il doit y avoir au moins une marque par défaut. Définissez une autre marque comme défaut avant de modifier celle-ci.')
          return false
        }
      }
      await updateVendeurMarque(id, brandData.name, brandData.isDefault)
      await fetchBrands(true)
      return true
    } catch (err) {
      error.value = 'Erreur lors de la modification'
      throw err
    }
  }

  const deleteBrand = async (id: string, targetBrandId?: string) => {
    try {
      // Interdire la suppression si c'est la dernière marque restante
      if (brands.value.length <= 1) {
        throw new Error('Vous ne pouvez pas supprimer votre dernière marque. Il doit y avoir au moins une marque par défaut.')
      }
      await deleteVendeurMarque(id, targetBrandId)
      await fetchBrands(true)
    } catch (err) {
      error.value = 'Erreur lors de la suppression'
      throw err
    }
  }

  return {
    brands,
    isLoading,
    error,
    fetchBrands,
    addBrand,
    updateBrand,
    deleteBrand
  }
}

const {
  brands,
  fetchBrands,
  addBrand,
  updateBrand,
  deleteBrand
} = useBrandStore()

const showModal = ref(false)
const showDeleteModal = ref(false)
const showReassignModal = ref(false)
const isEditing = ref(false)
const editingBrandId = ref<string | null>(null)
const brandToDelete = ref<Brand | null>(null)
const targetBrandId = ref('')
const isSubmitting = ref(false)
const isDeleting = ref(false)

const availableTargetBrands = computed(() => {
  if (!brandToDelete.value) return brands.value
  return brands.value.filter(b => b.id !== brandToDelete.value!.id)
})

const newBrand = reactive({
  name: '',
  isDefault: false
})

const modalTitle = computed(() => {
  return isEditing.value ? 'Modifier la marque' : 'Ajouter une nouvelle marque'
})

const brandColumns = [
  { key: 'number', title: '#', sortable: true, align: 'center' as const },
  { key: 'name', title: 'Nom de la marque', sortable: true, align: 'left' as const },
  { key: 'productCount', title: 'Nombre de produits', sortable: true, align: 'center' as const },
  { key: 'isDefault', title: 'Défaut', sortable: true, align: 'center' as const },
  { key: 'isActive', title: 'Statut', sortable: true, align: 'center' as const },
]

const isActivating = ref(false)
const statutUpdatingId = ref<string | null>(null)

const handleSetBrandStatus = async (brand: Brand, actif: boolean) => {
  if (statutUpdatingId.value || brand.isActive === actif) return
  statutUpdatingId.value = brand.id
  try {
    await setVendeurMarqueStatut(brand.id, actif)
    const target = brands.value.find((item) => item.id === brand.id)
    if (target) target.isActive = actif
    notification.success(actif ? 'Marque activée.' : 'Marque désactivée.')
  } catch (err) {
    notification.error(err instanceof Error ? err.message : 'Erreur lors du changement de statut.')
  } finally {
    statutUpdatingId.value = null
  }
}

const handleActivateBrands = async () => {
  if (isActivating.value || brands.value.length === 0) return
  isActivating.value = true
  try {
    const result = await activerVendeurMarques()
    await fetchBrands(true)
    if (result.statutBoutique === 1) {
      notification.success(
        result.nombreActivees > 0
          ? `${result.nombreActivees} marque(s) activée(s). Les marques sans produit visible restent désactivées.`
          : 'Aucune marque activée : aucun produit visible.',
      )
    } else {
      notification.success('La boutique est hors ligne. Toutes les marques ont été désactivées.')
    }
  } catch (err) {
    notification.error(err instanceof Error ? err.message : 'Erreur lors de l’activation des marques.')
  } finally {
    isActivating.value = false
  }
}

const openAddBrandModal = () => {
  isEditing.value = false
  editingBrandId.value = null
  showModal.value = true
  resetForm()
}

const editBrand = (row: Brand) => {
  isEditing.value = true
  editingBrandId.value = row.id
  newBrand.name = row.name
  newBrand.isDefault = row.isDefault
  showModal.value = true
}

const resetForm = () => {
  newBrand.name = ''
  newBrand.isDefault = false
}

const handleSubmit = async () => {
  if (isSubmitting.value) return
  const nameTrimmed = newBrand.name.trim()
  if (!nameTrimmed) {
    notification.error('Le nom de la marque est requis.')
    return
  }

  // Empêcher les doublons (insensible à la casse et aux espaces)
  const exists = brands.value.some(b => b.name.trim().toLowerCase() === nameTrimmed.toLowerCase() && (!isEditing.value || b.id !== editingBrandId.value))
  if (!isEditing.value && exists) {
    notification.error('Cette marque existe déjà dans votre liste.')
    return
  }
  if (isEditing.value && exists) {
    notification.error('Une autre marque avec ce nom existe déjà.')
    return
  }

  isSubmitting.value = true
  try {
    if (isEditing.value && editingBrandId.value) {
      const updated = await updateBrand(editingBrandId.value, {
        name: nameTrimmed,
        isDefault: newBrand.isDefault
      })
      if (updated === false) return
    } else {
      await addBrand({
        name: nameTrimmed,
        isDefault: newBrand.isDefault
      })
    }
    
    showModal.value = false
    resetForm()
  } catch (err) {
    console.error('Erreur lors de la soumission:', err)
  } finally {
    isSubmitting.value = false
  }
}

const handleDeleteBrand = (row: Brand) => {
  // Protection : empêcher la suppression de la marque par défaut
  if (row.isDefault) {
    notification.error('La marque par défaut ne peut pas être supprimée. Veuillez d\'abord définir une autre marque comme défaut.')
    return
  }
  // Protection : empêcher la suppression de la dernière marque
  if (brands.value.length <= 1) {
    notification.error('Vous ne pouvez pas supprimer votre dernière marque. Il doit y avoir au moins une marque par défaut.')
    return
  }

  brandToDelete.value = row
  targetBrandId.value = ''

  if ((row.productCount ?? 0) > 0) {
    const defaultBrand = brands.value.find(b => b.isDefault)
    if (defaultBrand && defaultBrand.id !== row.id) {
      targetBrandId.value = defaultBrand.id
    } else {
      const firstOther = availableTargetBrands.value[0]
      targetBrandId.value = firstOther?.id ?? ''
    }
    showReassignModal.value = true
  } else {
    showDeleteModal.value = true
  }
}

const closeReassignModal = () => {
  showReassignModal.value = false
  brandToDelete.value = null
  targetBrandId.value = ''
}

const confirmDelete = async () => {
  if (!brandToDelete.value) return

  if (brandToDelete.value.isDefault) {
    notification.error('La marque par défaut ne peut pas être supprimée.')
    showDeleteModal.value = false
    brandToDelete.value = null
    return
  }

  isDeleting.value = true
  try {
    await deleteBrand(brandToDelete.value.id)
    showDeleteModal.value = false
    brandToDelete.value = null
    notification.success('Marque supprimée avec succès.')
  } catch (err) {
    console.error('Erreur lors de la suppression:', err)
    notification.error(err instanceof Error ? err.message : 'Erreur lors de la suppression de la marque.')
  } finally {
    isDeleting.value = false
  }
}

const confirmDeleteWithReassign = async () => {
  if (!brandToDelete.value || !targetBrandId.value) {
    notification.error('Veuillez sélectionner une marque de destination.')
    return
  }

  isDeleting.value = true
  try {
    await deleteBrand(brandToDelete.value.id, targetBrandId.value)
    closeReassignModal()
    notification.success('Produits réassignés et marque supprimée avec succès.')
  } catch (err) {
    console.error('Erreur lors de la suppression avec réassignation:', err)
    notification.error(err instanceof Error ? err.message : 'Erreur lors de la suppression de la marque.')
  } finally {
    isDeleting.value = false
  }
}

onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  fetchBrands()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(PageHeader), {
      title: "Liste de vos marques",
      class: "mb-4"
    }),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(ActionButton), {
          label: "Ajouter une nouvelle marque",
          icon: "fas fa-plus",
          variant: "primary",
          onClick: openAddBrandModal
        }),
        _createVNode(_unref(ActionButton), {
          label: "Activer les marques",
          icon: "fas fa-check",
          variant: "success",
          disabled: _unref(brands).length === 0 || isActivating.value,
          loading: isActivating.value,
          onClick: handleActivateBrands
        }, null, 8, ["disabled", "loading"])
      ]),
      (_unref(brands).length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_unref(ReusableTable), {
              columns: brandColumns,
              data: _unref(brands),
              sortable: true,
              "show-edit": true,
              "show-delete": true
            }, {
              "column-isDefault": _withCtx(({ row }) => [
                _createElementVNode("span", _hoisted_4, _toDisplayString(row.isDefault ? 'Oui' : 'Non'), 1)
              ]),
              "column-isActive": _withCtx(({ row }) => [
                _createElementVNode("span", {
                  class: _normalizeClass(["inline-flex items-center px-2 py-1 rounded-full text-xs font-medium", row.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-black'])
                }, _toDisplayString(row.isActive ? 'Activée' : 'Désactivée'), 3)
              ]),
              "column-productCount": _withCtx(({ row }) => [
                _createElementVNode("span", {
                  class: _normalizeClass(["inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full text-sm font-medium", (row.productCount ?? 0) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-black'])
                }, _toDisplayString(row.productCount ?? 0), 3)
              ]),
              actions: _withCtx(({ row }) => [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(BrandStatusSwitch, {
                    active: Boolean(row.isActive),
                    disabled: statutUpdatingId.value === String(row.id),
                    onToggle: ($event: any) => (handleSetBrandStatus(row as unknown as Brand, !row.isActive))
                  }, null, 8, ["active", "disabled", "onToggle"]),
                  _createElementVNode("button", {
                    onClick: _withModifiers(($event: any) => (editBrand(row as unknown as Brand)), ["stop"]),
                    class: "inline-flex h-8 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100",
                    title: "Modifier"
                  }, _cache[7] || (_cache[7] = [
                    _createElementVNode("i", { class: "fas fa-pen text-[10px]" }, null, -1),
                    _createTextVNode(" Modifier ")
                  ]), 8, _hoisted_6),
                  (!row.isDefault)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        onClick: _withModifiers(($event: any) => (handleDeleteBrand(row as unknown as Brand)), ["stop"]),
                        class: "inline-flex h-8 items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-100",
                        title: "Supprimer"
                      }, _cache[8] || (_cache[8] = [
                        _createElementVNode("i", { class: "fas fa-times text-[10px]" }, null, -1),
                        _createTextVNode(" Supprimer ")
                      ]), 8, _hoisted_7))
                    : (_openBlock(), _createElementBlock("span", _hoisted_8, _cache[9] || (_cache[9] = [
                        _createElementVNode("i", { class: "fas fa-lock text-[10px]" }, null, -1),
                        _createTextVNode(" Par défaut ")
                      ])))
                ])
              ]),
              _: 1
            }, 8, ["data"])
          ]))
        : _createCommentVNode("", true),
      (_unref(brands).length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(brands), (brand) => {
              return (_openBlock(), _createElementBlock("div", {
                key: brand.id,
                class: "bg-white rounded-lg border border-gray-200 shadow-sm p-4"
              }, [
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("h3", _hoisted_12, _toDisplayString(brand.name), 1),
                    (brand.isDefault)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_13, " Par défaut "))
                      : _createCommentVNode("", true)
                  ]),
                  _createVNode(BrandStatusSwitch, {
                    active: brand.isActive,
                    disabled: statutUpdatingId.value === brand.id,
                    onToggle: ($event: any) => (handleSetBrandStatus(brand, !brand.isActive))
                  }, null, 8, ["active", "disabled", "onToggle"])
                ]),
                _createElementVNode("div", _hoisted_14, [
                  _cache[10] || (_cache[10] = _createElementVNode("span", null, "Statut", -1)),
                  _createElementVNode("span", {
                    class: _normalizeClass(["inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium", brand.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-black'])
                  }, _toDisplayString(brand.isActive ? 'Activée' : 'Désactivée'), 3)
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _cache[11] || (_cache[11] = _createElementVNode("span", null, "Nombre de produits", -1)),
                  _createElementVNode("span", {
                    class: _normalizeClass(["inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-sm font-medium", (brand.productCount ?? 0) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-black'])
                  }, _toDisplayString(brand.productCount ?? 0), 3)
                ]),
                _createElementVNode("div", _hoisted_16, [
                  _createElementVNode("button", {
                    onClick: ($event: any) => (editBrand(brand)),
                    class: "inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 text-xs font-medium text-blue-700"
                  }, _cache[12] || (_cache[12] = [
                    _createElementVNode("i", { class: "fas fa-pen text-[10px]" }, null, -1),
                    _createTextVNode(" Modifier ")
                  ]), 8, _hoisted_17),
                  (!brand.isDefault)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        onClick: ($event: any) => (handleDeleteBrand(brand)),
                        class: "inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 text-xs font-medium text-red-600"
                      }, _cache[13] || (_cache[13] = [
                        _createElementVNode("i", { class: "fas fa-times text-[10px]" }, null, -1),
                        _createTextVNode(" Supprimer ")
                      ]), 8, _hoisted_18))
                    : (_openBlock(), _createElementBlock("span", _hoisted_19, _cache[14] || (_cache[14] = [
                        _createElementVNode("i", { class: "fas fa-lock text-[10px]" }, null, -1),
                        _createTextVNode(" Par défaut ")
                      ])))
                ])
              ]))
            }), 128))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_20, _cache[15] || (_cache[15] = [
            _createElementVNode("p", { class: "text-black" }, "Aucune marque trouvée.", -1)
          ]))),
      _createVNode(_unref(ModalComp), {
        isVisible: showModal.value,
        title: modalTitle.value,
        size: "md",
        "onUpdate:isVisible": _cache[3] || (_cache[3] = ($event: any) => (showModal.value = false))
      }, {
        body: _withCtx(() => [
          _createElementVNode("form", {
            onSubmit: _withModifiers(handleSubmit, ["prevent"]),
            class: "space-y-4"
          }, [
            _createVNode(_unref(BaseInput), {
              modelValue: newBrand.name,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newBrand.name) = $event)),
              label: "Nom de la marque",
              required: ""
            }, null, 8, ["modelValue"]),
            _createElementVNode("div", _hoisted_21, [
              _createVNode(_unref(BaseInput), {
                modelValue: newBrand.isDefault,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newBrand.isDefault) = $event)),
                label: "Marque par défaut",
                type: "checkbox"
              }, null, 8, ["modelValue"])
            ])
          ], 32)
        ]),
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_22, [
            _createVNode(_unref(ActionButton), {
              label: "Annuler",
              variant: "secondary",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (showModal.value = false))
            }),
            _createVNode(_unref(ActionButton), {
              label: "Enregistrer",
              variant: "primary",
              disabled: isSubmitting.value || !newBrand.name.trim(),
              loading: isSubmitting.value,
              onClick: handleSubmit
            }, null, 8, ["disabled", "loading"])
          ])
        ]),
        _: 1
      }, 8, ["isVisible", "title"]),
      _createVNode(_unref(ModalComp), {
        isVisible: showDeleteModal.value,
        title: "Confirmer la suppression",
        size: "sm",
        "onUpdate:isVisible": _cache[5] || (_cache[5] = ($event: any) => (showDeleteModal.value = false))
      }, {
        body: _withCtx(() => [
          _createElementVNode("div", _hoisted_23, [
            _cache[16] || (_cache[16] = _createElementVNode("div", { class: "mb-4" }, [
              _createElementVNode("i", { class: "fas fa-exclamation-triangle text-red-500 text-4xl" })
            ], -1)),
            _cache[17] || (_cache[17] = _createElementVNode("p", { class: "text-black mb-2" }, " Êtes-vous sûr de vouloir supprimer la marque ", -1)),
            _createElementVNode("p", _hoisted_24, " \"" + _toDisplayString(brandToDelete.value?.name) + "\" ? ", 1),
            _cache[18] || (_cache[18] = _createElementVNode("p", { class: "text-sm text-black" }, " Cette action est irréversible. ", -1))
          ])
        ]),
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_25, [
            _createVNode(_unref(ActionButton), {
              label: "Annuler",
              variant: "secondary",
              onClick: _cache[4] || (_cache[4] = ($event: any) => (showDeleteModal.value = false))
            }),
            _createVNode(_unref(ActionButton), {
              label: "Supprimer",
              variant: "danger",
              onClick: confirmDelete
            })
          ])
        ]),
        _: 1
      }, 8, ["isVisible"]),
      _createVNode(_unref(ModalComp), {
        isVisible: showReassignModal.value,
        title: "Réassigner les produits",
        size: "md",
        "onUpdate:isVisible": closeReassignModal
      }, {
        body: _withCtx(() => [
          _createElementVNode("div", null, [
            _cache[23] || (_cache[23] = _createElementVNode("div", { class: "mb-4 text-center" }, [
              _createElementVNode("i", { class: "fas fa-exchange-alt text-amber-500 text-4xl" })
            ], -1)),
            _createElementVNode("p", _hoisted_26, [
              _cache[19] || (_cache[19] = _createTextVNode(" La marque ")),
              _createElementVNode("span", _hoisted_27, "\"" + _toDisplayString(brandToDelete.value?.name) + "\"", 1),
              _cache[20] || (_cache[20] = _createTextVNode(" est associée à ")),
              _createElementVNode("span", _hoisted_28, _toDisplayString(brandToDelete.value?.productCount ?? 0) + " produit(s)", 1),
              _cache[21] || (_cache[21] = _createTextVNode(". "))
            ]),
            _cache[24] || (_cache[24] = _createElementVNode("p", { class: "text-sm text-black mb-4 text-center" }, " Choisissez une autre marque pour réassigner tous les produits concernés avant la suppression. ", -1)),
            _cache[25] || (_cache[25] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, [
              _createTextVNode(" Marque de destination "),
              _createElementVNode("span", { class: "text-red-500" }, "*")
            ], -1)),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((targetBrandId).value = $event)),
              class: "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            }, [
              _cache[22] || (_cache[22] = _createElementVNode("option", {
                value: "",
                disabled: ""
              }, "Sélectionner une marque", -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableTargetBrands.value, (brand) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: brand.id,
                  value: brand.id
                }, _toDisplayString(brand.name) + _toDisplayString(brand.isDefault ? ' (par défaut)' : ''), 9, _hoisted_29))
              }), 128))
            ], 512), [
              [_vModelSelect, targetBrandId.value]
            ])
          ])
        ]),
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_30, [
            _createVNode(_unref(ActionButton), {
              label: "Annuler",
              variant: "secondary",
              onClick: closeReassignModal
            }),
            _createVNode(_unref(ActionButton), {
              label: "Réassigner et supprimer",
              variant: "danger",
              disabled: !targetBrandId.value || isDeleting.value,
              loading: isDeleting.value,
              onClick: confirmDeleteWithReassign
            }, null, 8, ["disabled", "loading"])
          ])
        ]),
        _: 1
      }, 8, ["isVisible"])
    ])
  ], 64))
}
}

})